import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { privateAPI } from '../util/api'; // Для защищенных запросов

const Profile = () => {
  const [user, setUser] = useState(null); // Для хранения данных пользователя
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false); // Для отображения формы смены пароля
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false); // Для отображения предупреждения об удалении аккаунта

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await privateAPI.get('/user'); // Запрос для получения информации о пользователе
        setUser(response.data);
      } catch (error) {
        setError('Failed to fetch user information.');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Проверка на совпадение нового пароля с подтверждением
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }

    try {
      const accessToken = localStorage.getItem('access_token'); // Получаем access token
      if (!accessToken) {
        setError('Access token is missing.');
        return;
      }

      // Отправляем запрос на сервер для смены пароля, передавая токен в URL
      const response = await privateAPI.post(`/register/reset-password/${accessToken}`, {
        password: newPassword,
      });

      if (response.status === 200) {
        setSuccessMessage('Password changed successfully.');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowChangePassword(false);

        // Редирект на страницу логина через 2 секунды
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      setError('Failed to change password. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await privateAPI.delete('/user'); // Запрос на удаление аккаунта
      localStorage.removeItem('access_token'); // Очищаем токены
      localStorage.removeItem('refresh_token');
      setSuccessMessage('Account deleted successfully.');

      // Редирект на главную страницу через 2 секунды
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      setError('Failed to delete account. Please try again.');
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Profile
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        {loading && <p className="text-center">Loading...</p>}
        {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
        {successMessage && <p className="text-green-500 mb-4 text-center">{successMessage}</p>}

        {!loading && user && (
          <>
            <div className="mb-6 text-center">
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>User ID:</strong> {user.id}</p>
            </div>

            <button
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="block w-full bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-indigo-500 focus:outline-none"
            >
              {showChangePassword ? 'Cancel' : 'Change Password'}
            </button>

            {showChangePassword && (
              <form onSubmit={handleChangePassword} className="mt-6 space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium leading-6 text-gray-900">
                    New Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmNewPassword" className="block text-sm font-medium leading-6 text-gray-900">
                    Confirm New Password
                  </label>
                  <div className="mt-2">
                    <input
                      id="confirmNewPassword"
                      name="confirmNewPassword"
                      type="password"
                      required
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="block w-full bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-indigo-500 focus:outline-none"
                >
                  Change Password
                </button>
              </form>
            )}

            <div className="mt-6">
              <button
                onClick={() => setShowDeleteConfirmation(true)}
                className="block w-full bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-red-500 focus:outline-none"
              >
                Delete Account
              </button>
            </div>

            {showDeleteConfirmation && (
              <div className="mt-4 p-4 bg-red-100 border border-red-400 rounded">
                <p className="text-center text-red-700">
                  Are you sure you want to delete your account? All your webhooks and logs will be permanently removed.
                </p>
                <div className="mt-4 flex justify-center space-x-4">
                  <button
                    onClick={handleDeleteAccount}
                    className="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-red-500 focus:outline-none"
                  >
                    Yes, delete my account
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirmation(false)}
                    className="bg-gray-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-gray-500 focus:outline-none"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;